import React, { useRef, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  IconButton,
  TextField,
  FormControl,
  Radio,
  Autocomplete
} from '@mui/material';
import { DeleteOutlined } from '@mui/icons-material';
import {
  SPREADSHEET_COLUMN_TYPES,
  SPREADSHEET_COLUMN_TYPE_LABELS,
  type SpreadsheetColumnType,
  type SpreadsheetColumnTypeMap
} from '../../../constants/spreadsheetColumnTypes';

/** Format source column key for display when no label is provided: no underscores, title case. */
function formatSourceColumnDisplay(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

interface ColumnMappingTableProps {
  targetFields: string[];
  columnMapping: Record<string, string | null>;
  columnTypes: SpreadsheetColumnTypeMap;
  detectedColumns: string[];
  /** Optional display labels for source columns (e.g. HubSpot field label). */
  detectedColumnLabels?: Record<string, string>;
  protectedFields: string[];
  triggeredFromProspectPage: boolean;
  newColumnName: string;
  newColumnType: SpreadsheetColumnType;
  onMappingChange: (field: string, value: string | null) => void;
  onColumnTypeChange: (field: string, type: SpreadsheetColumnType) => void;
  onRemoveField: (field: string) => void;
  onNewColumnNameChange: (name: string) => void;
  onNewColumnTypeChange: (type: SpreadsheetColumnType) => void;
  onAddColumn: (nameOverride?: string) => void;
  /** When true, shows a "Primary" radio column (exactly one selection). */
  enablePrimarySelection?: boolean;
  /** Which campaign column is currently primary */
  primaryField?: string;
  /** Called when a row is selected as primary */
  onPrimaryFieldChange?: (field: string) => void;
}

const ColumnMappingTable: React.FC<ColumnMappingTableProps> = ({
  targetFields,
  columnMapping,
  columnTypes,
  detectedColumns,
  detectedColumnLabels,
  protectedFields,
  triggeredFromProspectPage,
  newColumnName,
  newColumnType,
  onMappingChange,
  onColumnTypeChange,
  onRemoveField,
  onNewColumnNameChange,
  onNewColumnTypeChange,
  onAddColumn,
  enablePrimarySelection = false,
  primaryField,
  onPrimaryFieldChange
}) => {
  const getSourceColumnLabel = (key: string): string =>
    detectedColumnLabels?.[key] ?? formatSourceColumnDisplay(key);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevTargetFieldsLength = useRef<number | null>(null);

  useEffect(() => {
    const fieldsLength = targetFields.length;
    const prev = prevTargetFieldsLength.current;
    prevTargetFieldsLength.current = fieldsLength;
    // Only scroll when user explicitly adds one column (not on initial load from [] to many)
    if (prev !== null && fieldsLength === prev + 1 && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [targetFields.length, columnMapping]);

  return (
    <TableContainer
      ref={containerRef}
      sx={{ maxHeight: '400px', overflow: 'auto' }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold' }}>
              Campaign Table Column
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Column Type</TableCell>
            {enablePrimarySelection && (
              <TableCell
                sx={{ fontWeight: 'bold' }}
                align="center"
                width="80px"
              >
                Primary
              </TableCell>
            )}
            <TableCell sx={{ fontWeight: 'bold' }}>Source Column</TableCell>
            {!triggeredFromProspectPage && (
              <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {targetFields.map((fieldName) => (
            <TableRow key={fieldName}>
              <TableCell>
                {fieldName.startsWith('$') ? fieldName.slice(1) : fieldName}
              </TableCell>
              <TableCell>
                <FormControl fullWidth size="small">
                  <Select
                    value={columnTypes[fieldName] ?? 'str'}
                    onChange={(e) =>
                      onColumnTypeChange(
                        fieldName,
                        e.target.value as SpreadsheetColumnType
                      )
                    }
                  >
                    {SPREADSHEET_COLUMN_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>
                        {SPREADSHEET_COLUMN_TYPE_LABELS[t]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </TableCell>
              {enablePrimarySelection && (
                <TableCell align="center" width="80px">
                  <Radio
                    name="primaryColumnSelection"
                    checked={
                      !!columnMapping[fieldName] && primaryField === fieldName
                    }
                    onChange={() => onPrimaryFieldChange?.(fieldName)}
                    disabled={!columnMapping[fieldName]}
                    size="small"
                    sx={{
                      color: (theme) => theme.customColors?.themePrimary,
                      '&.Mui-checked': {
                        color: (theme) => theme.customColors?.themePrimary
                      }
                    }}
                  />
                </TableCell>
              )}
              <TableCell sx={{ minWidth: 260 }}>
                <Autocomplete
                  size="small"
                  options={detectedColumns}
                  getOptionLabel={(option) => getSourceColumnLabel(option)}
                  value={columnMapping[fieldName] ?? ''}
                  onChange={(_, newValue) =>
                    onMappingChange(
                      fieldName,
                      (newValue as string | null) || null
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="None"
                      size="small"
                      sx={{
                        '& .MuiInputBase-root': {
                          minHeight: 40,
                          minWidth: 260,
                          backgroundColor: !(columnMapping[fieldName] ?? '')
                            ? (theme) =>
                                theme.palette.mode === 'dark'
                                  ? theme.palette.grey[800]
                                  : theme.palette.grey[300]
                            : undefined
                        }
                      }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option === value}
                />
              </TableCell>
              {!triggeredFromProspectPage && (
                <TableCell>
                  {!protectedFields.includes(fieldName) && (
                    <IconButton
                      size="small"
                      onClick={() => onRemoveField(fieldName)}
                    >
                      <DeleteOutlined />
                    </IconButton>
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
          {!triggeredFromProspectPage && (
            <TableRow>
              <TableCell>
                <TextField
                  placeholder="New column name"
                  size="small"
                  value={newColumnName}
                  onChange={(e) => onNewColumnNameChange(e.target.value)}
                  onBlur={(e) => {
                    const value = (e.target as HTMLInputElement).value?.trim();
                    if (value) onAddColumn(value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const value = (
                        e.target as HTMLInputElement
                      ).value?.trim();
                      if (value) {
                        e.preventDefault();
                        onAddColumn(value);
                      }
                    }
                  }}
                  sx={{ width: '100%' }}
                />
              </TableCell>
              <TableCell>
                <FormControl fullWidth size="small">
                  <Select
                    value={newColumnType}
                    onChange={(e) =>
                      onNewColumnTypeChange(
                        e.target.value as SpreadsheetColumnType
                      )
                    }
                  >
                    {SPREADSHEET_COLUMN_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>
                        {SPREADSHEET_COLUMN_TYPE_LABELS[t]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </TableCell>
              {enablePrimarySelection && <TableCell />}
              <TableCell />
              <TableCell />
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ColumnMappingTable;
